import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { URLLoader } from 'src/app/main/configs/URLLoader';
import CatalogItem from 'src/app/main/models/Book';
import { Department } from 'src/app/main/models/Department';
import { Row } from 'src/app/main/models/Row';
import { Shelf } from 'src/app/main/models/Shelf';
import Writer from 'src/app/main/models/Writer';
import Publisher from 'src/app/main/models/Publisher';
import { HTTPService } from 'src/app/main/services/HTTPService';
import CONFIG from 'src/app/main/urls/urls';

@Component({
  selector: 'app-edit-book',
  templateUrl: './edit-book.component.html',
  styleUrls: ['./edit-book.component.css'],
})
export class EditBookComponent extends URLLoader implements OnInit {
  @Input() catalogItem: CatalogItem;

  writers: any[] = [];
  publishers: any[] = [];
  departments: any[] = [];
  shelves: any[] = [];
  rows: any[] = [];
  mediaTypes: any[] = [];
  physicalDescriptions: any[] = [];
  categories: any[] = [];

  constructor(
    public toastr: ToastrService,
    public activeModal: NgbActiveModal,
    private catalogItemService: HTTPService
  ) {
    super();
  }

  ngOnInit(): void {
    // If catalogItem was passed in with only an ID (e.g. from book-list),
    // fetch the full object so all dropdowns pre-select correctly.
    if (this.catalogItem?.id) {
      this.catalogItemService
        .getAll(CONFIG.URL_BASE + '/book/' + this.catalogItem.id)
        .subscribe(
          (data: CatalogItem) => {
            this.catalogItem = data;
          },
          (err: HttpErrorResponse) => {
            this.toastr.error('Failed to load book: ' + err.message, 'Error');
          }
        );
    }

    this.loadDropdowns();
  }

  loadDropdowns(): void {
    this.catalogItemService.getWriters().subscribe(
      (writers: Writer[]) => { this.writers = writers; },
      err => console.error('Error fetching writers:', err)
    );

    this.catalogItemService.getPublishers().subscribe(
      (publishers: Publisher[]) => { this.publishers = publishers; },
      err => console.error('Error fetching publishers:', err)
    );

    this.catalogItemService.getDepartments().subscribe(
      (departments: Department[]) => { this.departments = departments; },
      err => console.error('Error fetching departments:', err)
    );

    this.catalogItemService.getShelves().subscribe(
      (shelves: Shelf[]) => { this.shelves = shelves; },
      err => console.error('Error fetching shelves:', err)
    );

    this.catalogItemService.getRows().subscribe(
      (rows: Row[]) => { this.rows = rows; },
      err => console.error('Error fetching rows:', err)
    );

    this.catalogItemService.getMediaTypes().subscribe(
      (mediaTypes: any[]) => { this.mediaTypes = mediaTypes; },
      err => console.error('Error fetching media types:', err)
    );

    this.catalogItemService.getPhysicalDescriptions().subscribe(
      (physicalDescriptions: any[]) => { this.physicalDescriptions = physicalDescriptions; },
      err => console.error('Error fetching physical descriptions:', err)
    );

    this.catalogItemService.getCategories().subscribe(
      (categories: any[]) => { this.categories = categories; },
      err => console.error('Error fetching categories:', err)
    );
  }

  saveCatalogItem(): void {
    console.log(this.catalogItem);

    if (this.validateCatalogForm(this.catalogItem)) {
      // Resolve full objects from dropdown lists
      this.catalogItem.mediaType     = this.mediaTypes.find(x => x.id === this.catalogItem.mediaType?.id);
      this.catalogItem.departement   = this.departments.find(x => x.id === this.catalogItem.departement?.id);
      this.catalogItem.row           = this.rows.find(x => x.id === this.catalogItem.row?.id);
      this.catalogItem.writer        = this.writers.find(x => x.id === this.catalogItem.writer?.id);
      this.catalogItem.shelf         = this.shelves.find(x => x.id === this.catalogItem.shelf?.id);
      this.catalogItem.publisher     = this.publishers.find(x => x.id === this.catalogItem.publisher?.id);
      this.catalogItem.category      = this.categories.find(x => x.id === this.catalogItem.category?.id);

      this.catalogItemService
        .create(CONFIG.URL_BASE + '/book/create', this.catalogItem)
        .then(() => {
          this.toastr.success('Item updated successfully!', 'Success');
          this.activeModal.close(this.catalogItem);
        });
    }
  }

  onCancelClick(): void {
    this.activeModal.dismiss();
  }

  validateCatalogForm(catalogItem: any): boolean {
    const errors: Record<string, string> = {};

    if (!catalogItem.isbn || catalogItem.isbn.trim().length < 10) {
      errors.isbn = 'ISBN is required and must be at least 10 characters long.';
    }
    if (!catalogItem.title || catalogItem.title.trim().length < 3) {
      errors.title = 'Title is required and must be at least 3 characters long.';
    }
    if (!catalogItem.edition || catalogItem.edition.trim().length < 1) {
      errors.edition = 'Edition is required.';
    }
    if (!catalogItem.edition_year || isNaN(+catalogItem.edition_year) || +catalogItem.edition_year > new Date().getFullYear()) {
      errors.edition_year = 'Edition Year must be a valid year not in the future.';
    }
    if (!catalogItem.publishing_year || isNaN(+catalogItem.publishing_year) || +catalogItem.publishing_year > new Date().getFullYear()) {
      errors.publishing_year = 'Publishing Year must be a valid year not in the future.';
    }
    if (!catalogItem.publication_place || catalogItem.publication_place.trim().length < 3) {
      errors.publication_place = 'Publication Place must be at least 3 characters long.';
    }
    if (!catalogItem.number_of_pages || catalogItem.number_of_pages <= 0) {
      errors.number_of_pages = 'Number of Pages must be greater than 0.';
    }
    if (!catalogItem.quantity || isNaN(+catalogItem.quantity) || +catalogItem.quantity <= 0) {
      errors.quantity = 'Quantity is required and must be greater than 0.';
    }

    Object.entries(errors).forEach(([field, message]) => {
      console.error(`${field}: ${message}`);
      this.toastr?.error(message, 'Validation Error');
    });

    return Object.keys(errors).length === 0;
  }
}